/**
 * Moderation Service
 * API layer for unified admin moderation dashboard
 * Solis Games - Phase 2 Implementation
 */

import { supabase } from './supabase';

export const moderationService = {
  /**
   * Get moderation queue with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  async getModerationQueue(filters = {}) {
    try {
      const { data, error } = await supabase.rpc('get_moderation_queue_with_context', {
        p_status: filters.status === 'all' ? null : filters.status,
        p_severity: filters.severity || null,
        p_game_id: filters.gameId || null,
        p_content_type: filters.contentType || null,
        p_limit: filters.limit || 50,
        p_offset: filters.offset || 0
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching moderation queue:', error);
      return { data: [], error: error.message };
    }
  },

  /**
   * Get moderation statistics
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  async getModerationStats() {
    try {
      const [
        { count: pendingCount },
        { count: criticalCount },
        { count: inReviewCount },
        { count: todayResolved }
      ] = await Promise.all([
        supabase.from('moderation_queue').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('moderation_queue').select('id', { count: 'exact', head: true }).eq('severity', 'critical').eq('status', 'pending'),
        supabase.from('moderation_queue').select('id', { count: 'exact', head: true }).eq('status', 'in_review'),
        supabase.from('moderation_queue').select('id', { count: 'exact', head: true }).in('status', ['approved', 'rejected']).gte('resolved_at', new Date().toISOString().split('T')[0])
      ]);

      // Calculate avg response time
      const { data: responseData } = await supabase
        .from('moderation_queue')
        .select('flagged_at, reviewed_at')
        .not('reviewed_at', 'is', null)
        .gte('flagged_at', new Date(Date.now() - 86400000).toISOString());

      let avgResponseTime = 0;
      if (responseData && responseData.length > 0) {
        const totalTime = responseData.reduce((sum, item) => {
          return sum + (new Date(item.reviewed_at) - new Date(item.flagged_at));
        }, 0);
        avgResponseTime = Math.floor(totalTime / responseData.length / 60000);
      }

      return {
        data: {
          pendingCount: pendingCount || 0,
          criticalCount: criticalCount || 0,
          inReviewCount: inReviewCount || 0,
          todayResolved: todayResolved || 0,
          avgResponseTime
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching moderation stats:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Perform moderation action
   * @param {string} queueId - Moderation queue item ID
   * @param {string} moderatorId - Moderator user ID
   * @param {string} action - Action type
   * @param {string} status - New status
   * @param {string} notes - Optional notes
   * @returns {Promise<{data: any, error: string|null}>}
   */
  async performAction(queueId, moderatorId, action, status, notes = null) {
    try {
      const { data, error } = await supabase.rpc('update_moderation_status', {
        p_queue_id: queueId,
        p_moderator_id: moderatorId,
        p_status: status,
        p_action: action,
        p_notes: notes
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error performing moderation action:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Get user violation history
   * @param {string} userId - User ID
   * @param {number} limit - Max results
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  async getUserViolationHistory(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('moderation_queue')
        .select(`
          *,
          game:games(id, title),
          moderator:user_profiles!moderator_id(full_name)
        `)
        .eq('reported_user_id', userId)
        .order('flagged_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user violation history:', error);
      return { data: [], error: error.message };
    }
  },

  /**
   * Get action history for queue item
   * @param {string} queueId - Queue item ID
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  async getActionHistory(queueId) {
    try {
      const { data, error } = await supabase
        .from('moderation_actions_log')
        .select(`
          *,
          moderator:user_profiles!moderator_id(full_name, email, avatar_url)
        `)
        .eq('moderation_queue_id', queueId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching action history:', error);
      return { data: [], error: error.message };
    }
  },

  /**
   * Get conversation context for chat messages
   * @param {string} messageId - Message ID
   * @param {string} channelId - Channel ID
   * @param {number} limit - Max messages
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  async getChatContext(messageId, channelId, limit = 20) {
    try {
      // Get the flagged message
      const { data: flaggedMessage } = await supabase
        .from('chat_messages')
        .select('created_at')
        .eq('id', messageId)
        .single();

      if (!flaggedMessage) return { data: [], error: 'Message not found' };

      // Get messages before and after
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          id,
          user_id,
          message,
          created_at,
          user:user_profiles(full_name, email)
        `)
        .eq('channel_id', channelId)
        .is('deleted_at', null)
        .gte('created_at', new Date(new Date(flaggedMessage.created_at).getTime() - 600000).toISOString()) // 10 min before
        .lte('created_at', new Date(new Date(flaggedMessage.created_at).getTime() + 600000).toISOString()) // 10 min after
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching chat context:', error);
      return { data: [], error: error.message };
    }
  },

  /**
   * Subscribe to real-time queue updates
   * @param {Function} callback - Callback function
   * @returns {Object} Subscription object
   */
  subscribeToQueueUpdates(callback) {
    return supabase
      .channel('moderation_queue_changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'moderation_queue' },
        callback
      )
      .subscribe();
  },

  /**
   * Delete content (mark as deleted)
   * @param {Object} item - Queue item
   * @returns {Promise<{success: boolean, error: string|null}>}
   */
  async deleteContent(item) {
    try {
      if (item.content_type === 'chat_message') {
        const { error } = await supabase
          .from('chat_messages')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', item.content_id);

        if (error) throw error;
      } else if (item.content_type === 'leaderboard_score') {
        const { error } = await supabase
          .from('leaderboard_entries')
          .update({ flagged_for_review: true })
          .eq('id', item.content_id);

        if (error) throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting content:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Warn user (send notification)
   * @param {Object} item - Queue item
   * @param {string} notes - Warning notes
   * @returns {Promise<{success: boolean, error: string|null}>}
   */
  async warnUser(item, notes) {
    try {
      const { error} = await supabase.from('notifications').insert({
        user_id: item.reported_user_id,
        type: 'moderation_warning',
        title: 'Content Moderation Warning',
        message: `Your content was flagged for: ${item.flag_reason}. ${notes || ''}`,
        metadata: { moderation_queue_id: item.id }
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error warning user:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Ban user
   * @param {Object} item - Queue item
   * @param {string} moderatorId - Moderator ID
   * @param {string} notes - Ban reason
   * @returns {Promise<{success: boolean, error: string|null}>}
   */
  async banUser(item, moderatorId, notes) {
    try {
      // Create ban record (if chat-related)
      if (item.content_type === 'chat_message') {
        const { error: banError } = await supabase.from('chat_bans').insert({
          user_id: item.reported_user_id,
          game_id: item.game_id,
          ban_type: 'permanent',
          reason: `Moderation action: ${item.flag_reason}. ${notes || ''}`,
          banned_by: moderatorId,
          is_active: true
        });

        if (banError) throw banError;
      }

      // Update user status to suspended
      const { error: statusError } = await supabase
        .from('user_profiles')
        .update({
          status: 'suspended',
          updated_at: new Date().toISOString()
        })
        .eq('id', item.reported_user_id);

      if (statusError) throw statusError;

      return { success: true, error: null };
    } catch (error) {
      console.error('Error banning user:', error);
      return { success: false, error: error.message };
    }
  }
};
